import React from "react";
import { ApiError, cockpit, withTenantContext, type DraftView, type OutboundDraftAttachmentView } from "@/api";
import { Button, Dialog } from "@/ds";
import { Icon } from "@/lib";
import { useT } from "../../i18n";
import { attachmentKind, formatByteSize } from "./attachment-strip";
import styles from "./email-review.module.css";

const ACCEPTED_TYPES = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";

function normalizedFilename(name: string): string {
  return name.normalize("NFKC").trim().toLocaleLowerCase("en-US");
}

function errorCode(error: unknown): string | null {
  if (!(error instanceof ApiError) || !error.data || typeof error.data !== "object") return null;
  const code = (error.data as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

export function OutboundAttachments({
  draft,
  editing,
  onChanged,
  tools,
}: {
  draft: DraftView;
  editing: boolean;
  onChanged?: () => Promise<void> | void;
  tools?: { before?: React.ReactNode; after?: React.ReactNode; expanded?: React.ReactNode };
}) {
  const t = useT();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const operationRef = React.useRef<string | null>(null);
  const [status, setStatus] = React.useState<"idle" | "uploading" | "validating" | "removing">("idle");
  const [cancellationPending, setCancellationPending] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [retryable, setRetryable] = React.useState(false);
  const [pendingFiles, setPendingFiles] = React.useState<readonly File[]>([]);
  const [pendingConfirmReplacements, setPendingConfirmReplacements] = React.useState(false);
  const [replacementFiles, setReplacementFiles] = React.useState<readonly File[] | null>(null);
  const [removeTarget, setRemoveTarget] = React.useState<OutboundDraftAttachmentView | null>(null);
  const attachments = draft.attachments ?? [];
  const mutable = Boolean(onChanged && draft.attachments_mutable && !editing);
  const busy = status !== "idle";

  React.useEffect(() => () => {
    const operationId = operationRef.current;
    if (operationId) {
      void cockpit.cancelDraftAttachmentUpload(draft.id, operationId, draft.version).catch(() => undefined);
    }
    abortRef.current?.abort();
  }, [draft.id, draft.version]);

  const upload = React.useCallback(async (files: readonly File[], confirmReplacements: boolean) => {
    if (!mutable || busy || files.length === 0) return;
    const controller = new AbortController();
    const operationId = crypto.randomUUID();
    abortRef.current = controller;
    operationRef.current = operationId;
    setPendingFiles(files);
    setPendingConfirmReplacements(confirmReplacements);
    setError(null);
    setRetryable(false);
    setProgress(0);
    setStatus("uploading");
    if (confirmReplacements) setReplacementFiles(null);
    try {
      await cockpit.uploadDraftAttachments(draft.id, files, draft.version, {
        operationId,
        confirmReplacements,
        signal: controller.signal,
        onProgress: (loaded, total) => setProgress(total > 0 ? Math.min(100, Math.round(loaded / total * 100)) : 0),
        onUploadComplete: () => setStatus("validating"),
      });
      setPendingFiles([]);
      setPendingConfirmReplacements(false);
      setReplacementFiles(null);
      if (inputRef.current) inputRef.current.value = "";
      await onChanged?.();
      setStatus("idle");
    } catch (caught) {
      setStatus("idle");
      if ((caught instanceof DOMException && caught.name === "AbortError")
        || errorCode(caught) === "ATTACHMENT_UPLOAD_CANCELLED") {
        setPendingFiles([]);
        setPendingConfirmReplacements(false);
        setReplacementFiles(null);
        setError(null);
        return;
      }
      if (errorCode(caught) === "ATTACHMENT_REPLACEMENT_CONFIRMATION_REQUIRED") {
        setReplacementFiles(files);
        return;
      }
      setError(caught instanceof Error ? caught.message : String(caught));
      const canRetry = caught instanceof ApiError && (caught.status === 0 || caught.status >= 500);
      setRetryable(canRetry);
      if (!canRetry) {
        setPendingFiles([]);
        setPendingConfirmReplacements(false);
        setReplacementFiles(null);
        if (inputRef.current) inputRef.current.value = "";
      }
    } finally {
      abortRef.current = null;
      if (operationRef.current === operationId) operationRef.current = null;
    }
  }, [busy, draft.id, draft.version, mutable, onChanged]);

  const cancelUpload = React.useCallback(async () => {
    const operationId = operationRef.current;
    if (!operationId || cancellationPending) return;
    setCancellationPending(true);
    setError(null);
    try {
      const outcome = await cockpit.cancelDraftAttachmentUpload(draft.id, operationId, draft.version);
      if (outcome.cancelled) {
        abortRef.current?.abort();
      } else if (outcome.committed && operationRef.current === operationId) {
        operationRef.current = null;
        abortRef.current?.abort();
        await onChanged?.();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setCancellationPending(false);
    }
  }, [cancellationPending, draft.id, draft.version, onChanged]);

  const cancelReplacement = React.useCallback(() => {
    setReplacementFiles(null);
    setPendingFiles([]);
    setPendingConfirmReplacements(false);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const choose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.currentTarget.files ?? [])];
    if (files.length === 0) return;
    const currentNames = new Set(attachments.map((item) => normalizedFilename(item.filename)));
    if (files.some((file) => currentNames.has(normalizedFilename(file.name)))) {
      setPendingFiles(files);
      setReplacementFiles(files);
      return;
    }
    void upload(files, false);
  };

  const remove = async () => {
    if (!removeTarget || !mutable || busy) return;
    setStatus("removing");
    setError(null);
    try {
      await cockpit.removeDraftAttachment(draft.id, removeTarget.id, draft.version);
      setRemoveTarget(null);
      await onChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setStatus("idle");
    }
  };

  const picker = onChanged && (
    <label className={styles.attachmentPicker} aria-disabled={!mutable || busy} title={t("ws.outboundAttachments.limits")}>
      <Icon name="paperclip" size={16} />
      <span>{t("ws.outboundAttachments.add")}</span>
      <input
        ref={inputRef}
        data-testid="outbound-attachment-picker"
        type="file"
        multiple
        accept={ACCEPTED_TYPES}
        disabled={!mutable || busy}
        onChange={choose}
      />
    </label>
  );
  return (
    <section className={styles.outboundAttachments} aria-label={t("ws.outboundAttachments.title", { n: attachments.length })} data-testid="outbound-attachments">
      {tools ? <>
        <div className={styles.draftTools}>{tools.before}{picker}{tools.after}</div>
        {tools.expanded}
        {attachments.length > 0 && <p className={styles.attachmentHint}>{t("ws.outboundAttachments.title", { n: attachments.length })}</p>}
      </> : <div className={styles.outboundAttachmentsHeader}>
        <div>
          <h3>{t("ws.outboundAttachments.title", { n: attachments.length })}</h3>
          <p>{t("ws.outboundAttachments.limits")}</p>
        </div>
        {picker}
      </div>}

      {editing && <p className={styles.attachmentHint} role="status">{t("ws.outboundAttachments.editBlocked")}</p>}
      {attachments.length === 0 ? (
        !tools && <p className={styles.attachmentHint}>{t("ws.outboundAttachments.empty")}</p>
      ) : (
        <ul className={styles.outboundAttachmentList} data-testid="outbound-attachment-list">
          {attachments.map((attachment) => (
            <li key={attachment.id} data-testid="outbound-attachment-item">
              <div>
                <strong>{attachment.filename}</strong>
                <span>{attachmentKind(attachment.content_type)} · {formatByteSize(attachment.byte_size)}</span>
                {!attachment.available && <span className={styles.attachmentUnavailable}>{t("ws.outboundAttachments.unavailable")}</span>}
              </div>
              <div className={styles.outboundAttachmentActions}>
                {attachment.available && (
                  <a className={styles.attachmentDownload} href={withTenantContext(attachment.download_url)} download>
                    {t("ws.outboundAttachments.download")}
                  </a>
                )}
                {onChanged && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!mutable || busy}
                    onClick={() => setRemoveTarget(attachment)}
                  >
                    {t("ws.outboundAttachments.remove")}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {(status === "uploading" || status === "validating") && (
        <div className={styles.attachmentProgress} role="status" aria-live="polite">
          <div data-testid="attachment-progress-track">
            <span
              data-testid="attachment-progress-bar"
              style={{ transform: `scaleX(${(status === "validating" ? 100 : progress) / 100})` }}
            />
          </div>
          <span>{status === "validating" ? t("ws.outboundAttachments.validating") : t("ws.outboundAttachments.uploading", { n: String(progress) })}</span>
          <Button variant="ghost" size="sm" disabled={cancellationPending} onClick={() => void cancelUpload()}>
            {t("common.cancel")}
          </Button>
        </div>
      )}
      {error && (
        <div className={styles.attachmentError} role="alert">
          <span>{error}</span>
          {retryable && pendingFiles.length > 0 && (
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void upload(pendingFiles, pendingConfirmReplacements)}>
              {t("common.retry")}
            </Button>
          )}
        </div>
      )}

      <Dialog
        open={replacementFiles !== null}
        onClose={busy ? undefined : cancelReplacement}
        tone="warning"
        icon={<Icon name="refresh" size={20} />}
        title={t("ws.outboundAttachments.replaceTitle")}
        footer={<>
          <Button variant="ghost" disabled={busy} onClick={cancelReplacement}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={busy} onClick={() => replacementFiles && void upload(replacementFiles, true)}>{t("ws.outboundAttachments.replace")}</Button>
        </>}
      >
        <p>{t("ws.outboundAttachments.replaceBody")}</p>
        <ul>{replacementFiles?.map((file) => <li key={`${file.name}:${file.size}`}>{file.name} · {formatByteSize(file.size)}</li>)}</ul>
      </Dialog>

      <Dialog
        open={removeTarget !== null}
        onClose={busy ? undefined : () => setRemoveTarget(null)}
        tone="warning"
        icon={<Icon name="trash" size={20} />}
        title={t("ws.outboundAttachments.removeTitle")}
        footer={<>
          <Button variant="ghost" disabled={busy} onClick={() => setRemoveTarget(null)}>{t("common.cancel")}</Button>
          <Button variant="destructive" disabled={busy} onClick={() => void remove()}>{t("ws.outboundAttachments.remove")}</Button>
        </>}
      >
        {removeTarget && <p>{removeTarget.filename} · {formatByteSize(removeTarget.byte_size)}</p>}
      </Dialog>
    </section>
  );
}
